'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { InfoModal } from '@/components/ui/info-modal';
import { DOCS_URL } from '@/config/docs-links';

interface CrossLink {
  label: ReactNode;
  onClick: () => void;
}

/** A cross-link with a stable key — required when passing multiple via `crossLinks`. */
interface KeyedCrossLink extends CrossLink {
  key: string;
}

interface DocsLinksProps {
  /** Optional single "What is …?" cross-link rendered to the left of the docs link. */
  crossLink?: CrossLink | undefined;
  /** Optional multiple cross-links (e.g. "What is graduation?" + "Preventing
   *  manipulation"), rendered in order before the docs link. Combined with
   *  `crossLink` when both are provided. Each needs a stable `key`. */
  crossLinks?: KeyedCrossLink[] | undefined;
  /** Optional docs sub-path (e.g. "/concepts/graduation") appended to DOCS_URL.
   *  Defaults to the docs home when omitted. */
  docsPath?: string;
}

/**
 * Canonical body links row for how-it-works modals: optional nested-explainer
 * cross-links (e.g. "What is graduation?", "Preventing manipulation") followed
 * by the "Read our docs" link. Rendered at the end of the modal body (below the
 * steps and the callout) — never in the footer. Centralises `DOCS_URL` + the
 * translated `common.read_docs` label, and deep-links each modal to its matching
 * docs page via `docsPath`.
 */
export function DocsLinks({ crossLink, crossLinks, docsPath }: DocsLinksProps): React.ReactNode {
  const t = useTranslations('common');
  const href = docsPath !== undefined ? `${DOCS_URL}${docsPath}` : DOCS_URL;
  const links: KeyedCrossLink[] = [
    ...(crossLink !== undefined ? [{ key: 'primary', ...crossLink }] : []),
    ...(crossLinks ?? []),
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {links.map((link) => (
        <InfoModal.CrossLink key={link.key} label={link.label} onClick={link.onClick} />
      ))}
      <InfoModal.DocsLink href={href} label={t('read_docs')} />
    </div>
  );
}
